/* eslint-disable */
import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  Text,
  Table,
  ScrollArea,
  Badge,
  Pagination,
  createStyles,
  Center,
  Flex,
  Select,
  Divider,
  Loader,
  Tooltip,
  Button,
  Group,
} from "@mantine/core";
import { X } from "tabler-icons-react";
import DateRangeSelector, { DateRange } from "utils/customDateRangeSelector";
import { useGetAttendanceEmployee } from "api/attendance/getAttendanceTable";
import { useGetScopedUsersList } from "api/users/getScopedUsersList";
import { useUser } from "hooks/useUser";
import { formatDateToDDMMYYYY, formatToLocalTime } from "utils/dateFormate";

const useStyles = createStyles((theme) => ({
  containerCard: {
    padding: "18px",
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[3]}`,
    boxShadow: "none",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: "12px",
    flexWrap: "wrap",
    gap: "16px",
  },
  titleLeft: {
    fontWeight: 600,
    color: theme.colors.dark[6],
  },
  controls: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  filtersContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  filterWrapper: {
    display: "flex",
    flexDirection: "column",
    height: "68px",
    justifyContent: "flex-end",
  },
  filterLabel: {
    marginBottom: "4px",
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
  },
  tableHeader: {
    backgroundColor:
      theme.colorScheme === "light" ? "#f1f4f6" : theme.colors.dark[7],
    fontWeight: 700,
    fontSize: "13px",
    color: theme.colors.gray[7],
  },
  rowCell: {
    paddingTop: "16px",
    paddingBottom: "16px",
    verticalAlign: "middle",
  },
  smallBadge: {
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 500,
  },
  productionBadge: {
    fontWeight: 600,
    fontSize: "12px",
  },
}));

interface EmployeeAttendanceTableProps {
  userId?: string | null;
}

type RangeOption =
  | "Today"
  | "Yesterday"
  | "Last 7 Days"
  | "Last 30 Days"
  | "This Month"
  | "Last Month"
  | "Custom Range";

export default function EmployeeAttendanceTable({ userId: propUserId }: EmployeeAttendanceTableProps) {
  const { classes } = useStyles();
  const { user } = useUser();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedType, setSelectedType] = useState<RangeOption>("Last 30 Days");
  const [filterType, setFilterType] = useState<string>("Last 30 Days");
  const [selectedRange, setSelectedRange] = useState<DateRange>({
    start: null,
    end: null,
  });

  const startDate = selectedRange.start;
  const endDate = selectedRange.end;

  // Check if user is manager/supervisor/admin
  const isManager = ["department_head", "supervisor", "admin", "superadmin"].includes(
    user?.role || ""
  );

  // Sync filterType with selectedType
  useEffect(() => {
    setFilterType(selectedType);
  }, [selectedType]);

  // Handle Date Range Change
  const handleDateChange = (range: DateRange) => {
    setSelectedRange(range);
    setPage(1);
  };

  const { data, isLoading } = useGetAttendanceEmployee(
    page,
    limit,
    filterType,
    startDate,
    endDate,
    propUserId || undefined
  );

  const records = data?.data?.records ?? [];
  const pagination = data?.data?.pagination;

  // Helper function to determine overtime status display
  const getOvertimeStatus = (isOvertime: boolean, isOvertimeApproved: boolean) => {
    if (!isOvertime) {
      return "-";
    }
    
    if (isOvertimeApproved) {
      return (
        <Badge color="teal" variant="light" className={classes.smallBadge}>
          Approved
        </Badge>
      );
    }
    
    return (
      <Badge color="orange" variant="light" className={classes.smallBadge}>
        Pending
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Center style={{ height: 200 }}>
        <Loader />
      </Center>
    );
  }

  return (
    <Card className={classes.containerCard}>
      {/* Header */}
      <div className={classes.headerRow}>
        <Text className={classes.titleLeft} size="lg">
          Employee Attendance
        </Text>

        {/* Filters */}
        <div className={classes.filtersContainer}>
          {/* Date Range */}
          <div className={classes.filterWrapper}>
            <Text className={classes.filterLabel}>Date Range</Text>
            <DateRangeSelector
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              onDateRangeChange={handleDateChange}
              selectedRange={selectedRange}
            />
          </div>
        </div>
      </div>

      <Divider mb="md" />

      {/* Table */}
      <ScrollArea>
        <Table highlightOnHover verticalSpacing="sm">
          <thead>
            <tr>
              <th className={classes.tableHeader}>Date</th>
              <th className={classes.tableHeader}>Clock In</th>
              <th className={classes.tableHeader}>Status</th>
              <th className={classes.tableHeader}>Clock Out</th>
              <th className={classes.tableHeader}>Break</th>
              <th className={classes.tableHeader}>Late</th>
              <th className={classes.tableHeader}>Overtime</th>
              <th className={classes.tableHeader}>Overtime Status</th>
              <th className={classes.tableHeader}>Production Hours</th>
            </tr>
          </thead>
          <tbody>
            {records.length > 0 ? (
              records.map((row:any) => (
                <tr key={row.id}>
                  <td className={classes.rowCell}>
                    <Text size="sm">{formatDateToDDMMYYYY(row.date?row.date:'')}</Text>
                  </td>

                  <td className={classes.rowCell}>
                    <Text size="sm">
                      {row.punchInTime ? formatToLocalTime(row.punchInTime) : "-"}
                    </Text>
                  </td>

                  <td className={classes.rowCell}>
                    <Badge
                      color={
                        row.status === "Present"
                          ? "green"
                          : row.status === "Leave"
                            ? "blue"
                            : "red"
                      }
                      variant="light"
                      className={classes.smallBadge}
                    >
                      {row.status}
                    </Badge>
                  </td>

                  <td className={classes.rowCell}>
                    <Text size="sm">
                      {row.punchOutTime
                        ? formatToLocalTime(row.punchOutTime)
                        : "-"}
                    </Text>
                  </td>

                  <td className={classes.rowCell}>
                    <Text size="sm">{row.breakTime ?? "-"}</Text>
                  </td>
                 
                  <td className={classes.rowCell}>
                    {row.isLate ? (
                      <Tooltip label={row.notes || "No notes available"} position="top">
                        <Text size="sm" c="green" fw={500} style={{ cursor: "pointer" }}>
                          Yes
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text size="sm" c="red" fw={500}>
                        No
                      </Text>
                    )}
                  </td>

                  <td className={classes.rowCell}>
                    <Text size="sm">{row.overtime ?? "-"}</Text>
                  </td>

                  {/* New Overtime Status Column */}
                  <td className={classes.rowCell}>
                    {getOvertimeStatus(row.isOvertime, row.isOvertimeApproved)}
                  </td>

                  <td className={classes.rowCell}>
                    <Badge
                      color={
                        Number(row.totalProduction) >= 8
                          ? "green"
                          : Number(row.totalProduction) < 7.5
                            ? "red"
                            : "orange"
                      }
                      variant="filled"
                      className={classes.productionBadge}
                    >
                      {row.totalProduction ? `${row.totalProduction}` : "-"}
                    </Badge>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9}>
                  <Center py="md">
                    <Text size="sm" color="dimmed">
                      No records found
                    </Text>
                  </Center>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollArea>

      {/* Pagination Footer */}
      {pagination && (
        <Flex justify="space-between" align="center" mt="md">
          <Text size="sm" color="dimmed">
            Showing{" "}
            {(pagination.currentPage - 1) * limit + 1}–
            {Math.min(pagination.currentPage * limit, pagination.totalRecords)}{" "}
            of {pagination.totalRecords} entries
          </Text>
          <Center>
            <Pagination
              total={pagination.totalPages}
              page={page}
              onChange={setPage}
              size="sm"
              siblings={1}
              boundaries={1}
            />
          </Center>
        </Flex>
      )}
    </Card>
  );
}